export const PRIMARY_THEME_STORAGE_KEY = 'erp-primary-theme';
export const PRIMARY_THEME_CUSTOM_COLOR_STORAGE_KEY = 'erp-primary-theme-custom-color';

export type PrimaryThemeId = 'blue' | 'emerald' | 'violet' | 'rose' | 'amber' | 'slate' | 'custom';

export interface PrimaryThemePreset {
  id: PrimaryThemeId;
  label: string;
  main: string;
  mainLight: string;
  mainDark: string;
  mainForeground: string;
  primary: string;
  primaryForeground: string;
  ring: string;
  sidebarPrimary: string;
  sidebarPrimaryForeground: string;
}

type RgbTuple = [number, number, number];

const DEFAULT_THEME_ID: PrimaryThemeId = 'blue';
const DEFAULT_CUSTOM_COLOR = '#023e8a';
const HEX_COLOR_PATTERN = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;

export const PRIMARY_THEME_PRESETS: Array<PrimaryThemePreset & { id: Exclude<PrimaryThemeId, 'custom'> }> = [
  {
    id: 'blue',
    label: 'Xanh dương',
    main: '2 62 138',
    mainLight: '3 83 164',
    mainDark: '1 46 102',
    mainForeground: '255 255 255',
    primary: '213 97% 27%',
    primaryForeground: '0 0% 100%',
    ring: '213 97% 35%',
    sidebarPrimary: '213 97% 27%',
    sidebarPrimaryForeground: '0 0% 100%'
  },
  {
    id: 'emerald',
    label: 'Xanh lục',
    main: '4 120 87',
    mainLight: '5 150 105',
    mainDark: '6 95 70',
    mainForeground: '255 255 255',
    primary: '163 94% 24%',
    primaryForeground: '0 0% 100%',
    ring: '163 94% 32%',
    sidebarPrimary: '163 94% 24%',
    sidebarPrimaryForeground: '0 0% 100%'
  },
  {
    id: 'violet',
    label: 'Tím',
    main: '91 33 182',
    mainLight: '109 40 217',
    mainDark: '76 29 149',
    mainForeground: '255 255 255',
    primary: '262 69% 42%',
    primaryForeground: '0 0% 100%',
    ring: '262 69% 50%',
    sidebarPrimary: '262 69% 42%',
    sidebarPrimaryForeground: '0 0% 100%'
  },
  {
    id: 'rose',
    label: 'Hồng đỏ',
    main: '190 18 60',
    mainLight: '225 29 72',
    mainDark: '159 18 57',
    mainForeground: '255 255 255',
    primary: '345 83% 41%',
    primaryForeground: '0 0% 100%',
    ring: '345 83% 49%',
    sidebarPrimary: '345 83% 41%',
    sidebarPrimaryForeground: '0 0% 100%'
  },
  {
    id: 'amber',
    label: 'Hổ phách',
    main: '180 83 9',
    mainLight: '217 119 6',
    mainDark: '146 64 14',
    mainForeground: '255 255 255',
    primary: '25 90% 37%',
    primaryForeground: '0 0% 100%',
    ring: '25 90% 45%',
    sidebarPrimary: '25 90% 37%',
    sidebarPrimaryForeground: '0 0% 100%'
  },
  {
    id: 'slate',
    label: 'Ghi xanh',
    main: '51 65 85',
    mainLight: '71 85 105',
    mainDark: '30 41 59',
    mainForeground: '255 255 255',
    primary: '215 33% 27%',
    primaryForeground: '0 0% 100%',
    ring: '215 33% 35%',
    sidebarPrimary: '215 33% 27%',
    sidebarPrimaryForeground: '0 0% 100%'
  }
];

const clamp = (value: number, min = 0, max = 255) => Math.min(max, Math.max(min, Math.round(value)));

const normalizeHexColor = (color?: string | null) => {
  const trimmedColor = color?.trim() || DEFAULT_CUSTOM_COLOR;
  const match = trimmedColor.match(HEX_COLOR_PATTERN);

  if (!match) return DEFAULT_CUSTOM_COLOR;

  const hex = match[1];
  const expandedHex =
    hex.length === 3
      ? hex
          .split('')
          .map(char => `${char}${char}`)
          .join('')
      : hex;

  return `#${expandedHex.toLowerCase()}`;
};

const hexToRgb = (hexColor: string): RgbTuple => {
  const normalizedHex = normalizeHexColor(hexColor).slice(1);
  return [
    Number.parseInt(normalizedHex.slice(0, 2), 16),
    Number.parseInt(normalizedHex.slice(2, 4), 16),
    Number.parseInt(normalizedHex.slice(4, 6), 16)
  ];
};

const rgbToTriplet = ([red, green, blue]: RgbTuple) => `${red} ${green} ${blue}`;

const rgbToHsl = ([red, green, blue]: RgbTuple) => {
  const r = red / 255;
  const g = green / 255;
  const b = blue / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const lightness = (max + min) / 2;
  const delta = max - min;

  if (delta === 0) return `0 0% ${Math.round(lightness * 100)}%`;

  const saturation = delta / (1 - Math.abs(2 * lightness - 1));
  let hue = 0;

  if (max === r) hue = ((g - b) / delta) % 6;
  if (max === g) hue = (b - r) / delta + 2;
  if (max === b) hue = (r - g) / delta + 4;

  return `${Math.round(hue * 60 < 0 ? hue * 60 + 360 : hue * 60)} ${Math.round(saturation * 100)}% ${Math.round(
    lightness * 100
  )}%`;
};

const mixRgb = (base: RgbTuple, target: RgbTuple, amount: number): RgbTuple => [
  clamp(base[0] + (target[0] - base[0]) * amount),
  clamp(base[1] + (target[1] - base[1]) * amount),
  clamp(base[2] + (target[2] - base[2]) * amount)
];

const getRelativeLuminance = ([red, green, blue]: RgbTuple) => {
  const [r, g, b] = [red, green, blue].map(value => {
    const channel = value / 255;
    return channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
  });

  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

const getContrastRatio = (base: RgbTuple, target: RgbTuple) => {
  const baseLuminance = getRelativeLuminance(base);
  const targetLuminance = getRelativeLuminance(target);
  const lighter = Math.max(baseLuminance, targetLuminance);
  const darker = Math.min(baseLuminance, targetLuminance);

  return (lighter + 0.05) / (darker + 0.05);
};

export const getReadableForeground = (background: RgbTuple) => {
  const white: RgbTuple = [255, 255, 255];
  const black: RgbTuple = [0, 0, 0];

  return getContrastRatio(background, white) >= getContrastRatio(background, black) ? white : black;
};

export const buildCustomPrimaryTheme = (color?: string | null): PrimaryThemePreset => {
  const mainRgb = hexToRgb(color || DEFAULT_CUSTOM_COLOR);
  const foregroundRgb = getReadableForeground(mainRgb);
  const primaryHsl = rgbToHsl(mainRgb);
  const foregroundHsl = foregroundRgb[0] === 255 ? '0 0% 100%' : '0 0% 0%';

  return {
    id: 'custom',
    label: 'Tùy chọn',
    main: rgbToTriplet(mainRgb),
    mainLight: rgbToTriplet(mixRgb(mainRgb, [255, 255, 255], 0.18)),
    mainDark: rgbToTriplet(mixRgb(mainRgb, [0, 0, 0], 0.24)),
    mainForeground: rgbToTriplet(foregroundRgb),
    primary: primaryHsl,
    primaryForeground: foregroundHsl,
    ring: primaryHsl,
    sidebarPrimary: primaryHsl,
    sidebarPrimaryForeground: foregroundHsl
  };
};

export const getPrimaryThemePreset = (themeId?: string | null, customColor?: string | null) => {
  if (themeId === 'custom') return buildCustomPrimaryTheme(customColor);
  return PRIMARY_THEME_PRESETS.find(preset => preset.id === themeId) ?? PRIMARY_THEME_PRESETS[0];
};

export const getStoredPrimaryThemeId = (): PrimaryThemeId => {
  if (typeof window === 'undefined') return DEFAULT_THEME_ID;

  const storedThemeId = window.localStorage.getItem(PRIMARY_THEME_STORAGE_KEY);
  if (storedThemeId === 'custom') return 'custom';

  return getPrimaryThemePreset(storedThemeId).id;
};

export const setStoredPrimaryThemeId = (themeId: PrimaryThemeId) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(PRIMARY_THEME_STORAGE_KEY, themeId);
};

export const getStoredCustomPrimaryColor = () => {
  if (typeof window === 'undefined') return DEFAULT_CUSTOM_COLOR;
  return normalizeHexColor(window.localStorage.getItem(PRIMARY_THEME_CUSTOM_COLOR_STORAGE_KEY));
};

export const setStoredCustomPrimaryColor = (color: string) => {
  if (typeof window === 'undefined') return DEFAULT_CUSTOM_COLOR;

  const normalizedColor = normalizeHexColor(color);
  window.localStorage.setItem(PRIMARY_THEME_CUSTOM_COLOR_STORAGE_KEY, normalizedColor);

  return normalizedColor;
};

export const applyPrimaryTheme = (themeId?: string | null, customColor?: string | null) => {
  if (typeof document === 'undefined') return getPrimaryThemePreset(themeId, customColor);

  const preset = getPrimaryThemePreset(themeId, customColor);
  const root = document.documentElement;

  root.style.setProperty('--main', preset.main);
  root.style.setProperty('--main-light', preset.mainLight);
  root.style.setProperty('--main-dark', preset.mainDark);
  root.style.setProperty('--main-foreground', preset.mainForeground);
  root.style.setProperty('--primary', preset.primary);
  root.style.setProperty('--primary-foreground', preset.primaryForeground);
  root.style.setProperty('--ring', preset.ring);
  root.style.setProperty('--sidebar-primary', preset.sidebarPrimary);
  root.style.setProperty('--sidebar-primary-foreground', preset.sidebarPrimaryForeground);

  return preset;
};
